/**
 * Search component with integrated data loading and virtualization
 * Unified version combining Search and SearchWithData
 * Migrated from front/src/components/Search.js
 */

import React, { useState } from 'react';
import { VariableSizeList } from 'react-window';
import { useTheme, styled } from '@mui/material/styles';
import {
  Autocomplete,
  ListSubheader,
  Typography,
  TextField,
  Popper,
  useMediaQuery,
  CircularProgress,
  Box
} from '@mui/material';
import { autocompleteClasses } from '@mui/material/Autocomplete';
import { filterOptions, getGroup } from '../utils/searchUtils.js';
import { isGroup, computeListHeight, GROUP_SIZE } from '../utils/listHeight.js';
import { useEntitiesIndex } from '../hooks/data/useEntitiesIndex';
import { homePage } from '../config/settings';

const LISTBOX_PADDING = 8; // px

/** Accessible name for the search input (placeholder alone is not an accessible name) */
export const SEARCH_INPUT_LABEL = 'Rechercher une commune ou un EPCI';

/**
 * Vertical space (px) reserved above and below the results frame when the
 * viewport is too short to honour the computed height. Covers the ~197px of
 * page above the listbox (header + search field) plus a bottom margin, so the
 * frame stays fully on-screen and its scrollbar reachable.
 */
const LISTBOX_VIEWPORT_RESERVE = 220;

/**
 * Get label from entity
 * @param {Object} entity - Entity object
 * @returns {string} Entity label
 */
const getLabel = (entity) => {
  if (typeof entity === 'string') return entity;
  return entity?.label || `${entity?.nom} (${entity?.code})`;
};

/**
 * Render individual row in the virtualized list
 * @param {Object} props - Row props from react-window
 * @returns {JSX.Element} Rendered row
 */
const renderRow = props => {
  const { data, index, style } = props;
  const dataSet = data[index];
  const inlineStyle = {
    ...style,
    top: style.top + LISTBOX_PADDING,
  };

  if (Object.hasOwn(dataSet, 'group')) {
    return (
      <ListSubheader key={dataSet.key} component="div" style={inlineStyle}>
        {dataSet.group}
      </ListSubheader>
    );
  }

  return (
    <Typography
      component="li"
      {...dataSet[0]}
      key={dataSet[0]?.key}
      style={inlineStyle}
      noWrap
    >
      {dataSet[1]}
    </Typography>
  );
};

const OuterElementContext = React.createContext({});

/**
 * Outer element for react-window
 * @param {Object} props - Component props
 * @returns {JSX.Element} Outer element
 */
const OuterElementType = React.forwardRef((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

/**
 * Use resets for virtualized list
 * @param {number} itemData - Item data length
 * @returns {Function} Reset function
 */
function useResetCache(itemData) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (ref.current !== null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [itemData]);
  return ref;
}

// Styled components for performance and consistency
const StyledPopper = styled(Popper)({
  [`& .${autocompleteClasses.listbox}`]: {
    boxSizing: 'border-box',
    // MUI defaults the listbox to maxHeight: 40vh, which silently clipped the
    // frame below the height computeListHeight asks for: on a 720px-tall
    // window 40vh is 288px, i.e. exactly 8 rows with the section headers
    // eating two slots (issue #228). The business rule must win, so 40vh has
    // to go -- but dropping the ceiling entirely is not an option either: the
    // frame is a fixed 400px starting ~197px down the page, so below a ~600px
    // viewport its last rows fall off-screen, out of reach (react-window's
    // inner scroll cannot help when the container itself overflows).
    // Hence a last-resort ceiling: the computed height wins on normal windows,
    // the viewport wins on short ones, where the scrollbar takes over.
    maxHeight: `calc(100vh - ${LISTBOX_VIEWPORT_RESERVE}px)`,
    // The 8px top/bottom padding MUI adds is accounted for by the
    // LISTBOX_PADDING term of the height passed to VariableSizeList.
    padding: 0,
    '& ul': {
      padding: 0,
      margin: 0,
    },
  },
});

/**
 * Virtualized listbox for large datasets
 * @param {Object} props - Listbox props
 * @returns {JSX.Element} Virtualized listbox
 */
const ListboxComponent = React.forwardRef(function ListboxComponent(props, ref) {
  const { children, ...other } = props;
  const itemData = [];
  children.forEach((item) => {
    itemData.push(item);
    itemData.push(...(item.children || []));
  });

  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up('sm'), {
    noSsr: true,
  });

  const itemCount = itemData.length;
  const itemSize = smUp ? 36 : 48;

  const getChildSize = (child) => {
    if (isGroup(child)) {
      return GROUP_SIZE;
    }
    return itemSize;
  };

  const gridRef = useResetCache(itemCount);

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          itemData={itemData}
          height={computeListHeight(itemData, itemSize) + 2 * LISTBOX_PADDING}
          width="100%"
          ref={gridRef}
          outerElementType={OuterElementType}
          innerElementType="ul"
          itemSize={(index) => getChildSize(itemData[index])}
          overscanCount={5}
          itemCount={itemCount}
        >
          {renderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

/**
 * Search component with data integration and virtualization
 * @param {Object} props - Component props
 * @param {Function} props.onSelectionChange - Callback when selection changes
 * @param {Function} props.renderInput - Custom input renderer
 * @param {string} props.placeholder - Input placeholder
 * @param {string} props.variant - Input variant
 * @param {boolean} props.hideDropdownIcon - Hide the dropdown arrow icon
 * @param {Object} props.sx - Custom styles
 * @param {Object} props.inputProps - Input props
 * @returns {JSX.Element} Search component
 */
const Search = ({
  onSelectionChange,
  renderInput,
  placeholder = homePage.searchPlaceholder,
  variant = "outlined",
  hideDropdownIcon = false,
  sx = {},
  inputProps = {},
  ...otherProps
}) => {
  const [inputValue, setInputValue] = useState('');
  const { data: entities = [], isLoading, error } = useEntitiesIndex();

  const defaultRenderInput = (params) => (
    <TextField
      {...params}
      placeholder={placeholder}
      variant={variant}
      fullWidth
      inputProps={{
        ...params.inputProps,
        'aria-label': SEARCH_INPUT_LABEL,
      }}
      InputProps={{
        ...params.InputProps,
        ...inputProps,
        endAdornment: (
          <React.Fragment>
            {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
            {params.InputProps.endAdornment}
          </React.Fragment>
        ),
      }}
    />
  );

  if (error) {
    return (
      <Box sx={{ p: 2, color: 'error.main' }}>
        Erreur lors du chargement des données de recherche
      </Box>
    );
  }

  return (
    <>
      <Autocomplete
        sx={{
          width: '100%',
          ...sx,
        }}
        disableListWrap
        PopperComponent={StyledPopper}
        ListboxComponent={ListboxComponent}
        options={entities}
        groupBy={(option) => getGroup(option)}
        getOptionLabel={(option) => getLabel(option)}
        filterOptions={filterOptions}
        inputValue={inputValue}
        onInputChange={(event, newInputValue) => {
          setInputValue(newInputValue);
        }}
        onChange={(event, newValue) => {
          if (onSelectionChange) {
            onSelectionChange(newValue);
          }
        }}
        renderInput={renderInput || defaultRenderInput}
        renderOption={(props, option) => [props, getLabel(option)]}
        renderGroup={(params) => params}
        loading={isLoading}
        loadingText="Chargement..."
        noOptionsText="Aucun résultat"
        popupIcon={hideDropdownIcon ? true : undefined}
        {...otherProps}
      />
      <Typography variant="body1" sx={{ textAlign: 'center', lineHeight: 1.2, mt: 1 }}>
        Saisir une commune ou un EPCI de France hexagonale<br />
        ou une commune ultramarine
      </Typography>
    </>
  );
};

Search.displayName = 'Search';

export default Search;
